import { IAccountStorageService } from './IAccountStorageService';
import { createDefaultAccounts } from '../models/AccountModels';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Firebase Firestore implementation of IAccountStorageService
 */
export class FirebaseAccountService extends IAccountStorageService {
  /**
   * @param {string} userId - Firebase user ID
   */
  constructor(userId) {
    super();
    if (!userId) {
      throw new Error('userId is required for FirebaseAccountService');
    }
    this.userId = userId;
    this.accountsDocRef = doc(db, 'users', userId, 'data', 'accounts');
    this.monthlyDocRef = doc(db, 'users', userId, 'data', 'monthly');
  }

  /**
   * Load all account data from Firebase
   * @returns {Promise<import('../models/AccountModels').AccountData>}
   */
  async loadData() {
    try {
      const [accountsSnap, monthlySnap] = await Promise.all([
        getDoc(this.accountsDocRef),
        getDoc(this.monthlyDocRef)
      ]);

      const accounts = accountsSnap.exists()
        ? accountsSnap.data()
        : createDefaultAccounts();

      const monthlyData = monthlySnap.exists()
        ? (monthlySnap.data().entries || []).sort((a, b) => a.month.localeCompare(b.month))
        : [];

      return {
        accounts,
        monthlyData,
        lastUpdated: monthlySnap.exists() ? monthlySnap.data().lastUpdated : undefined
      };
    } catch (error) {
      console.error('Error loading data from Firebase:', error);
      throw error;
    }
  }

  /**
   * Save accounts data
   * @param {import('../models/AccountModels').Accounts} accounts
   * @returns {Promise<void>}
   */
  async saveAccounts(accounts) {
    try {
      await setDoc(this.accountsDocRef, accounts);
    } catch (error) {
      console.error('Error saving accounts to Firebase:', error);
      throw error;
    }
  }

  /**
   * Save monthly data
   * @param {import('../models/AccountModels').MonthlyEntry[]} monthlyData
   * @returns {Promise<void>}
   */
  async saveMonthlyData(monthlyData) {
    try {
      await setDoc(this.monthlyDocRef, {
        entries: monthlyData,
        lastUpdated: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving monthly data to Firebase:', error);
      throw error;
    }
  }

  /**
   * Add a new account
   * @param {import('../models/AccountModels').Account} account
   * @returns {Promise<import('../models/AccountModels').Account>}
   */
  async addAccount(account) {
    const data = await this.loadData();
    const accountList = account.type === 'asset' ? data.accounts.assets : data.accounts.debts;
    accountList.push(account);
    await this.saveAccounts(data.accounts);
    return account;
  }

  /**
   * Remove an account
   * @param {string} accountName
   * @param {string} type - 'asset' or 'debt'
   * @returns {Promise<void>}
   */
  async removeAccount(accountName, type) {
    const data = await this.loadData();
    if (type === 'asset') {
      data.accounts.assets = data.accounts.assets.filter(a => a.name !== accountName);
    } else {
      data.accounts.debts = data.accounts.debts.filter(a => a.name !== accountName);
    }
    await this.saveAccounts(data.accounts);
  }

  /**
   * Save a monthly entry
   * @param {import('../models/AccountModels').MonthlyEntry} entry
   * @returns {Promise<void>}
   */
  async saveMonthlyEntry(entry) {
    const data = await this.loadData();
    const existingIndex = data.monthlyData.findIndex(m => m.month === entry.month);

    if (existingIndex >= 0) {
      data.monthlyData[existingIndex] = entry;
    } else {
      data.monthlyData.push(entry);
      data.monthlyData.sort((a, b) => a.month.localeCompare(b.month));
    }

    await this.saveMonthlyData(data.monthlyData);
  }

  /**
   * Clear all data
   * @returns {Promise<void>}
   */
  async clearAll() {
    await Promise.all([
      setDoc(this.accountsDocRef, createDefaultAccounts()),
      setDoc(this.monthlyDocRef, {
        entries: [],
        lastUpdated: new Date().toISOString()
      })
    ]);
  }
}
